/// <amd-module name="Environments/EnvironmentDetails/Components/EnvironmentResourcesComponent" />
import React = require("react");

import { EnvironmentResource, EnvironmentResourceType } from "DistributedTask/Client/Contracts/TaskAgent";

import { EnvironmentJobStatus, IEnvironmentDeploymentJobRecord, IEnvironmentResourceObject } from "Environments/Client/EnvironmentContracts";
import { EnvironmentActionsCreator } from "Environments/EnvironmentDetails/ActionsCreators/EnvironmentActionsCreator";
import { FilterKeys, PageKey, PerformanceScenarios, getResourceIconUrl, getResourceName } from "Environments/EnvironmentDetails/Common/Utils";
import * as Resources from "Environments/EnvironmentDetails/Resources";
import { EnvironmentStore } from "Environments/EnvironmentDetails/Stores/EnvironmentStore";
import { IDeleteDialogProps, getDeleteDialog } from "Environments/EnvironmentsCommon/DeleteDialog";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentProperties, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { getResourcePageUrl, getResourceRoute } from "Environments/EnvironmentsCommon/Link";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { StoreManager } from "Environments/Flux/StoreManager";
import { getJobStatusIndicatorData } from "Environments/RunsCommon/Indicator";

import { TagsDialog } from "Environments/EnvironmentDetails/Components/ManageTagsDialog";
import { ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { localeFormat } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Card } from "VSSUI/Card";
import { IImageProps, Image } from "VSSUI/Image";
import { Observer } from "VSSUI/Observer";
import { ColumnMore, ITableBreakpoint, ITableColumn, ITableRow, SimpleTableCell, Table, TwoLineTableCell } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ZeroData } from "VSSUI/ZeroData";

import { Action, Area, FeatureNames } from "Environments/EnvironmentDetails/Common/Utils";
import { publishTelemetry } from "Environments/EnvironmentsCommon/Telemetry";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { DropdownFilterBarItem } from "VSSUI/Dropdown";
import { FilterBar } from "VSSUI/FilterBar";
import { IIconProps } from "VSSUI/Icon";
import { ListSelection } from "VSSUI/List";
import { IMenuProps } from "VSSUI/Menu";
import { Pill, PillSize, PillVariant } from "VSSUI/Pill";
import { Status, StatusSize } from "VSSUI/Status";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Tooltip } from "VSSUI/TooltipEx";
import { FILTER_CHANGE_EVENT, Filter, IFilterState } from "VSSUI/Utilities/Filter";

interface IProps extends IEnvironmentBaseComponentProperties {
    instanceId: string;
    environmentId: number;
    markTTICallback: (scenario: string) => void;
    addResourceButtonCallBack: () => void;
    isPageLoad: () => boolean;
    filterToggled: ObservableValue<boolean>;
    setFilterBarIconVisibility: (visible: boolean) => void;
    filter: Filter;
    resourceFilterApplied: (resourceFilterApplied: boolean) => void;
}

interface IState extends IEnvironmentBaseComponentState {
    itemProvider: ArrayItemProvider<ITableItem>;
    showZeroDataExperience: boolean;
    showManageTagDialog: ObservableValue<boolean>;
}

interface ITableItem extends IEnvironmentResourceObject { }

export class EnvironmentResourcesComponent extends EnvironmentBaseComponent<IProps, IState> {

    public getInstanceId(context: IVssComponentContext): string {
        return this.props.instanceId;
    }

    public constructor(props: IProps, context: IVssComponentContext) {
        super(props, context);

        this._actionCreator = ActionCreatorManager.GetActionCreator<EnvironmentActionsCreator>(EnvironmentActionsCreator, props.instanceId);
        this._store = StoreManager.GetStore<EnvironmentStore>(EnvironmentStore, props.instanceId);


        const environment = this._store.getEnvironment();
        const itemProvider = new ArrayItemProvider<ITableItem>(environment ? environment.resources : []);
        publishTelemetry(context.pageContext, Area, FeatureNames.EnvironmentView, { "Action": Action.ResourceTabNavigation, "EnvironmentId": props.environmentId.toString() });

        this.state = {
            itemProvider: itemProvider,
            errorMessages: {},
            showZeroDataExperience: environment && environment.resources && environment.resources.length > 0 ? false : true,
            showManageTagDialog: new ObservableValue(false)
        };

        this.props.filter.subscribe(this._onFilterApplied, FILTER_CHANGE_EVENT);
        this._store.addChangedListener(this._onEnvironmentResourcesStoreChange);

        this._sizableColumns = [
            {
                id: "name",
                name: Resources.ResourceColumnName,
                width: new ObservableValue(300),
                className: "environment-cell",
                renderCell: this._renderNameColumn,
                onSize: this._onSizeSizable
            },
            {
                id: "lastestjob",
                name: Resources.ResourceLatesJobColumnName,
                width: new ObservableValue(-250),
                className: "environment-cell",
                renderCell: this._renderLastestJobColumn,
                onSize: this._onSizeSizable
            }
        ];

        if (isFeatureFlagEnabled(this.context.pageContext, this._manageTagsFeatureFlag, false)) {
            this._sizableColumns.push(new ColumnMore(this._getMenuItems));
        }
    }

    public componentDidMount(): void {
        this.props.markTTICallback(PerformanceScenarios.EnvironmentResourcesPageLoad);
        this._actionCreator.getResourcesWithFilters(this.context.pageContext, undefined, EnvironmentResourceType.Generic | EnvironmentResourceType.Kubernetes | EnvironmentResourceType.VirtualMachine | EnvironmentResourceType.Undefined, EnvironmentJobStatus.None, true, true, true);
        super.componentDidMount();
    }

    public render(): JSX.Element {
        return (
            <div className="page-content page-content-top">
                {this._getFilterBar()}
                {this._getResourceView()}
                {this._renderManageTagDialog()}
            </div>
        );
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onEnvironmentResourcesStoreChange);
        this.props.filter.unsubscribe(this._onFilterApplied, FILTER_CHANGE_EVENT);
        super.componentWillUnmount();
    }

    private _getResourceView(): JSX.Element {
        if (this.state.showZeroDataExperience && this.props.filter.hasChangesToReset()) {
            return this._getZeroDataContentForFilters();
        } else {
            const resourcesProvider = this.state.itemProvider;
            return (
                <Card className="bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                    <Table<Partial<ITableItem>>
                        ariaLabel={Resources.Resources}
                        key={resourcesProvider.length}
                        columns={this._sizableColumns}
                        itemProvider={resourcesProvider}
                        onActivate={this._onRowClicked}
                        tableBreakpoints={this._getTableBreakPoints()}
                    />
                </Card>
            );
        }
    }

    private _onEnvironmentResourcesStoreChange = (): void => {
        const itemProvider = this._getItemProvider(this._store.getResources());
        this.props.setFilterBarIconVisibility(!this._shouldShowZeroDataPage() || this.props.filter.hasChangesToReset());
        this.props.resourceFilterApplied(this.props.filter.hasChangesToReset());
        this.setState({
            itemProvider: itemProvider,
            showZeroDataExperience: this._shouldShowZeroDataPage()
        }, () => {
            const resultCount = this.state.itemProvider.length;
            announce(resultCount > 0 ? format(Resources.FilteredDataCountText, resultCount) : Resources.NoResultsFound);
        });
    }

    private _shouldShowZeroDataPage(): boolean {
        return (
            this._store.getResources().length === 0 &&
            !this._messagesStore.getMessage(PageKey.EnvironmentView)
        );
    }

    private _getZeroDataContentForFilters(): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, "ms.vss-environments-web/environment-details/Images/no-results.png");
        return (
            <Card className="flex-grow bolt-card-no-vertical-padding environment-minimum-card-height" contentProps={{ contentPadding: false }}>
                <div className="zero-data-container">
                    <ZeroData
                        imageAltText={Resources.NoResultsFound}
                        imagePath={image}
                        secondaryText={Resources.NoResultsFound}
                        className={"zero-data-content"}
                    />
                </div>
            </Card>
        )
    }

    private _getItemProvider(resources: IEnvironmentResourceObject[] | undefined): ArrayItemProvider<ITableItem> {
        if (!resources) {
            return new ArrayItemProvider<ITableItem>([]);
        }

        return new ArrayItemProvider<ITableItem>(resources);
    }

    private _onRowClicked = (event: any, row: ITableRow<ITableItem>): void => {
        let environment = this._store.getEnvironment();
        if (environment && environment.resources && environment.resources[row.index].type != EnvironmentResourceType.VirtualMachine) {
            onClickFPS(this.context.pageContext, getResourcePageUrl(this.context.pageContext, this.props.environmentId, environment.resources[row.index].id, getResourceRoute(this._store.getEnvironment().resources[row.index].type)), true);
        }
    }

    private _onFilterApplied = (filterState: IFilterState): void => {
        let filtersApplied = this.props.filter.getState();
        const resourceName = this._getSearchTextValue(filtersApplied);
        const resourceType = this._getResourceTypeFilterValue(filtersApplied);
        const jobStatus = this._getStatusFilterValue(filtersApplied);
        publishTelemetry(this.context.pageContext, Area, FeatureNames.EnvironmentView, { "Action": Action.FilterApplied, "EnvironmentId": this.props.environmentId.toString(), "ResourceNameFilterApplied": resourceName ? "True" : "False", "ResourceTypeFilterApplied": resourceType ? "True" : "False", "LastJobStatusFilterApplied": jobStatus ? "True" : "False" });
        this._actionCreator.getResourcesWithFilters(this.context.pageContext, resourceName, resourceType, jobStatus, true, true, true);
    }

    private _getFilterBar(): JSX.Element {
        return (
            <ConditionalChildren renderChildren={this.props.filterToggled}>
                <FilterBar filter={this.props.filter}>
                    <KeywordFilterBarItem filterItemKey={FilterKeys.ResourceNameFilterKey} placeholder={Resources.SearchResourcesPlaceholder} throttleWait={400} />
                    <DropdownFilterBarItem
                        placeholder={Resources.ResourceTypeColumnName}
                        filterItemKey={FilterKeys.ResourceTypeFilterKey}
                        selection={this._resourceTypeSelection}
                        items={this._getResourceTypePickListItems()}
                    />
                    <DropdownFilterBarItem
                        placeholder={Resources.ResourceStatusColumnName}
                        filterItemKey={FilterKeys.DeploymentStatusFilterKey}
                        selection={this._stateSelection}
                        items={this._getStatusPickListItems()}
                    />
                </FilterBar>
                <div className={"env-filter-spacer"} />
            </ConditionalChildren>
        )
    }

    private _getSearchTextValue = (filterState: IFilterState): string | undefined => {
        let searchFilterValue: string = "";
        let filterValue = filterState && filterState[FilterKeys.ResourceNameFilterKey];
        if (filterValue) {
            searchFilterValue = filterValue && filterValue.value;
        }
        return !!searchFilterValue ? "*" + searchFilterValue + "*" : undefined;
    }

    private _getStatusFilterValue = (filterState: IFilterState): EnvironmentJobStatus => {
        let statusFilterValue: EnvironmentJobStatus = EnvironmentJobStatus.None;
        let statusValue = filterState && filterState[FilterKeys.DeploymentStatusFilterKey];
        let selectedStatusList = statusValue && statusValue.value;
        if (selectedStatusList) {
            selectedStatusList.forEach((status: string) => {
                if (status === Resources.DeploymentSucceeded) {
                    statusFilterValue |= EnvironmentJobStatus.Succeeded | EnvironmentJobStatus.SucceededWithIssues;
                } else if (status === Resources.DeploymentFailed) {
                    statusFilterValue |= EnvironmentJobStatus.Failed | EnvironmentJobStatus.Abandoned | EnvironmentJobStatus.Canceled | EnvironmentJobStatus.Skipped;
                } else {
                    statusFilterValue |= EnvironmentJobStatus.All;
                }
            });
        }

        return statusFilterValue;
    }

    private _getResourceTypeFilterValue = (filterState: IFilterState): EnvironmentResourceType => {
        let resourceTypeFilterValue: EnvironmentResourceType = EnvironmentResourceType.Undefined;
        let resourceTypeValue = filterState && filterState[FilterKeys.ResourceTypeFilterKey];
        let selectedStatusList = resourceTypeValue && resourceTypeValue.value;
        if (selectedStatusList && selectedStatusList.length > 0) {
            selectedStatusList.forEach((status: string) => {
                if (status === Resources.KubernetesResource) {
                    resourceTypeFilterValue |= EnvironmentResourceType.Kubernetes;
                } else if (status === Resources.VirtualMachineResource) {
                    resourceTypeFilterValue |= EnvironmentResourceType.VirtualMachine;
                } else {
                    resourceTypeFilterValue = EnvironmentResourceType.Generic | EnvironmentResourceType.Kubernetes | EnvironmentResourceType.VirtualMachine | EnvironmentResourceType.Undefined;
                }
            });
        } else {
            resourceTypeFilterValue = EnvironmentResourceType.Generic | EnvironmentResourceType.Kubernetes | EnvironmentResourceType.VirtualMachine | EnvironmentResourceType.Undefined;
        }

        return resourceTypeFilterValue;
    }

    private _getStatusPickListItems(): string[] {
        return [Resources.DeploymentSucceeded, Resources.DeploymentFailed];
    }

    private _getResourceTypePickListItems(): string[] {
        return [Resources.KubernetesResource, Resources.VirtualMachineResource];
    }

    private _renderNameColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        const resourceName = tableItem.name;
        const resourceType = tableItem.type;
        const resourceSourceProvider = tableItem.providerData ? tableItem.providerData.providerDisplayString : undefined;
        const imageProps: IImageProps = { src: getResourceIconUrl(this.context.pageContext, resourceType), width: 24, className: "icon-large-margin", alt: getResourceName(resourceType) };
        const iconProps: IIconProps = { render: () => <Tooltip text={getResourceName(resourceType)}>{Image(imageProps)}</Tooltip> };
        return (
            <TwoLineTableCell
                key={"environments-col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                iconProps={iconProps}
                line1={
                    <Tooltip overflowOnly={true} text={resourceName}>
                        <span className={"font-weight-semibold body-m text-ellipsis flex-row"}>
                            {
                                tableItem.tags && tableItem.tags.indexOf("Review") > -1
                                    ? <span className={"flex-row"}> 
                                        <span style={{ marginRight: "8px" }}>
                                            {resourceName} 
                                        </span>
                                        <Pill
                                            className={"font-weight-normal"}
                                            color={{ red: 255, green: 216, blue: 188 }}
                                            size={PillSize.compact}
                                            variant={PillVariant.colored}>
                                            Review
                                        </Pill>
                                    </span>
                                    : <span> {resourceName} </span>
                            }
                        </span>
                    </Tooltip>
                }
                line2={
                    <Tooltip overflowOnly={true} text={resourceSourceProvider}>
                        <span className={"secondary-text body-s text-ellipsis"}>{resourceSourceProvider}</span>
                    </Tooltip>
                }
            />
        )
    }

    private _onSizeSizable = (event: MouseEvent, index: number, width: number, column: ITableColumn<ITableItem>): void => {
        (column.width as ObservableValue<number>).value = width;
    }

    private _renderLastestJobColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        let lastCompletedRequestList: IEnvironmentDeploymentJobRecord[] | undefined = tableItem.deploymentRecords;
        if (!lastCompletedRequestList || lastCompletedRequestList.length === 0) {
            return (
                <SimpleTableCell 
                    key={"lastestjob-" + columnIndex} 
                    columnIndex={columnIndex} 
                    tableColumn={tableColumn}
                    ariaLabel={"lastestjob-" + columnIndex} 
                >
                    <Tooltip overflowOnly={true} text={Resources.NeverDeployed}>
                        <div className={"text-ellipsis"}>{Resources.NeverDeployed}</div>
                    </Tooltip>
                </SimpleTableCell>
            );
        }
        else {
            const status = getJobStatusIndicatorData(lastCompletedRequestList[0].result);
            const deploymentMessage = format(Resources.lastDeploymentStatusMessage, lastCompletedRequestList[0].owner.name, lastCompletedRequestList[0].definition.name);
            return (
                <SimpleTableCell key={"lastestjob-" + columnIndex} ariaLabel={"lastestjob-" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn} contentClassName="bolt-table-cell-content-with-inline-link no-v-padding fontSizeM">
                    <FPSLink className={"text-ellipsis bolt-table-link bolt-table-inline-link flex-row flex-center"} href={lastCompletedRequestList[0].owner._links.web.href} onClick={this._jobOnClick}>
                        <Tooltip text={status.label}>
                            <span className="flex-row">
                                <Status {...status.statusProps} className="environment-icon-margin" size={StatusSize.m} ariaLabel={status.label} />
                            </span>
                        </Tooltip>
                        <Tooltip overflowOnly={true} text={deploymentMessage}>
                            <span className="flex-row text-ellipsis">
                                {deploymentMessage}
                            </span>
                        </Tooltip>
                    </FPSLink>
                </SimpleTableCell>
            );
        }
    }

    private _getMenuItems = (resource: ITableItem): IMenuProps => {
        return {
            id: "resource-actions",
            items: [
                {
                    id: "manage-tags",
                    text: Resources.ManageTags,
                    onActivate: () => {
                        this._currentSelectedResource.value = resource as IEnvironmentResourceObject;
                        this.state.showManageTagDialog.value = true;
                    }
                },
                {
                    id: "delete",
                    text: Resources.Delete,
                    onActivate: () => {
                        this._currentSelectedResource.value = resource as IEnvironmentResourceObject;
                        this._onDeleteEnvironmentResourceClicked(resource);
                    }
                }
            ]
        };
    }

    private _onDeleteEnvironmentResourceClicked = (resource: IEnvironmentResourceObject): void => {
        
        const dialogProps: IDeleteDialogProps = {
            id: resource.id,
            headerText: localeFormat(Resources.DeleteDialogHeader, resource.name),
            message: Resources.DeleteEnvironmentResourceDialogText,
            onOkClick: this._onDeleteEnvironmentResourceDialogOkClick,
            onDismiss: () => null
        }

        getDeleteDialog(this.context.pageContext, dialogProps);
    }

    private _onDeleteEnvironmentResourceDialogOkClick = (): void => {
        let resource = this._currentSelectedResource.value as IEnvironmentResourceObject;
        let resourceId: number = resource.id;
        let resourceType: EnvironmentResourceType = resource.type;
        let environmentId: number = this.props.environmentId;

        publishTelemetry(
            this.context.pageContext,
            Area,
            FeatureNames.EnvironmentView,
            { 
                "Action": Action.EnvironmentDeleted,
                "EnvironmentResourceId": resourceId.toString() 
            });
        
        this._actionCreator.deleteEnvironmentResource(
            this.context.pageContext,
            environmentId,
            resourceId,
            resourceType);
    }

    private _renderManageTagDialog = (): JSX.Element => {
        return (
            <Observer resource={this._currentSelectedResource}>
                {(tagsDialogProps: { resource: IEnvironmentResourceObject | undefined }) => (
                    <TagsDialog
                        resource={tagsDialogProps.resource}
                        onSaveTags={this._onSaveTags}
                        getRemoteTags={() => new Array<string>()}
                        showTagsDialog={this.state.showManageTagDialog} />
                )}
            </Observer>);
    }

    private _getTableBreakPoints(): ITableBreakpoint[] {
        var breakpoints: ITableBreakpoint[];

        if (isFeatureFlagEnabled(this.context.pageContext, this._manageTagsFeatureFlag, false)) {

            breakpoints=  [
                {
                    breakpoint: ScreenBreakpoints.xsmall,
                    columnWidths: [-200, -200, 2.625]
                },
                {
                    breakpoint: ScreenBreakpoints.small,
                    columnWidths: [300, -250, 2.625]
                }
            ];
        } else {
            breakpoints = [
                {
                    breakpoint: ScreenBreakpoints.xsmall,
                    columnWidths: [-200, -200]
                },
                {
                    breakpoint: ScreenBreakpoints.small,
                    columnWidths: [300, -250]
                }
            ];
        }

        return breakpoints;
    }

    private _onSaveTags = (newTagsSet: string[]): Promise<EnvironmentResource> => {
        let resource = this._currentSelectedResource.value as IEnvironmentResourceObject;
        let resourceId: number = resource.id;
        let resourceType: EnvironmentResourceType = resource.type;
        let tags: Array<string> = new Array<string>();
        tags.push(...newTagsSet);
        return this._actionCreator.updateEnvironmentResourceTags(this.context.pageContext, resourceId, resourceType, tags);
    }

    private _sizableColumns: any[];

    private _jobOnClick = (event: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>): void => {
        publishTelemetry(this.context.pageContext, Area, FeatureNames.EnvironmentView, { "Action": Action.ResourcePageJobNavigation, "EnvironmentId": this._store.getEnvironment().id.toString() });
    }

    private _resourceTypeSelection: ListSelection = new ListSelection({ alwaysMerge: true, multiSelect: true, selectOnFocus: false });
    private _stateSelection: ListSelection = new ListSelection({ alwaysMerge: true, multiSelect: true, selectOnFocus: false });
    private _store: EnvironmentStore;
    private _currentSelectedResource: ObservableValue<IEnvironmentResourceObject | undefined> = new ObservableValue(undefined);
    private _actionCreator: EnvironmentActionsCreator;
    private _manageTagsFeatureFlag: string = "Pipelines.Environments.Resources.ManageTags";
}